import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { memo } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { useTheme } from "@mui/material/styles";
import type { ExplorerNode } from "../../declarations/IExplorerTypes";
import { getExistenceIndicator } from "./existenceIndicator";
import { getLineStatusChipStyle } from "./lineStatusColors";
import { getNodeColor, getNodeIcon } from "./nodeColors";
import { getNodeCardSx } from "./nodeCardStyles";
import NodeToolbar from "./NodeToolbar";

function ExistenceIcon({ existsInCS, existsInD365 }: { existsInCS?: boolean; existsInD365?: boolean }) {
    const indicator = getExistenceIndicator(existsInCS, existsInD365);
    if (!indicator) {
        return null;
    }
    const { tooltip, color, Icon } = indicator;
    return (
        <Tooltip title={tooltip} arrow>
            <Icon sx={{ fontSize: 14, color, display: "flex" }} />
        </Tooltip>
    );
}

function LineDetailNode({ data }: NodeProps<ExplorerNode>) {
    const theme = useTheme();
    const line = data.lineDetail;
    const nodeColor = getNodeColor(data.entityType);
    const isDark = theme.palette.mode === "dark";

    if (!line) {
        return null;
    }

    return (
        <>
            <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
            <Card
                elevation={0}
                sx={getNodeCardSx({ nodeColor, isSelected: data.isSelected, isDark, width: 220 })}
            >
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <NodeToolbar
                        nodeColor={nodeColor}
                        icon={getNodeIcon(data.entityType)}
                        title={data.nodeTitle}
                        subtitle={data.nodeSubtitle}
                        dataAreaId={line.dataAreaId}
                        onInfoClick={data.onSelect ? () => data.onSelect!(data.selectId ?? data.entityType) : undefined}
                        titleAdornment={
                            (line.existsInCS !== undefined || line.existsInD365 !== undefined)
                                ? <ExistenceIcon existsInCS={line.existsInCS} existsInD365={line.existsInD365} />
                                : undefined
                        }
                    />

                        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.8 }}>Status</Typography>
                                <Chip
                                    label={line.status}
                                    size="small"
                                    sx={{
                                        height: 16,
                                        fontSize: "0.65rem",
                                        fontWeight: 600,
                                        ...getLineStatusChipStyle(line.status, isDark),
                                    }}
                                />
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.8 }}>D365 Item</Typography>
                                <Typography variant="caption" fontWeight={500}>{line.itemNumber}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.8 }}>Net Price</Typography>
                                <Typography variant="caption" fontWeight={500}>
                                    {line.currencyCode} {line.lineAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.8 }}>Qty</Typography>
                                <Typography variant="caption" fontWeight={500}>
                                    {line.quantity}{line.salesUnitSymbol && line.salesUnitSymbol.toLowerCase() !== "ea" ? ` ${line.salesUnitSymbol}` : ""}
                                </Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.8 }}>Lot</Typography>
                                <Typography variant="caption" fontWeight={500}>{line.inventoryLotId || "\u2014"}</Typography>
                            </Stack>
                        </Stack>

                        {line.lineDescription && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75, lineHeight: 1.4, fontSize: "0.7rem" }}>
                                {line.lineDescription}
                            </Typography>
                        )}
                </CardContent>
            </Card>
            <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
            <Handle type="source" position={Position.Bottom} id="bottom" style={{ opacity: 0 }} />
        </>
    );
}

export default memo(LineDetailNode);
